/**
 * Source mapping system to track generated code origins
 * Maps generated properties back to their original source locations
 */

export interface SourceMapping {
  /** Generated property name (e.g., "Calculator_onAsd") */
  generatedName: string;
  /** Original source file path */
  sourceFile: string;
  /** Original property/field name in source */
  sourceName: string;
  /** Line number in source file */
  sourceLine?: number;
  /** Column number in source file */
  sourceColumn?: number;
  /** Module name */
  moduleName: string;
  /** Type of mapping (event, method, constant, etc.) */
  type: "event" | "method" | "constant" | "property";
}

export class SourceMapRegistry {
  private mappings: Map<string, SourceMapping> = new Map();

  /**
   * Add a source mapping
   */
  addMapping(mapping: SourceMapping): void {
    this.mappings.set(mapping.generatedName, mapping);
  }

  /**
   * Get source mapping by generated name
   */
  getMapping(generatedName: string): SourceMapping | undefined {
    return this.mappings.get(generatedName);
  }

  /**
   * Find mapping by partial match (e.g., "onAsd" matches "Calculator_onAsd")
   */
  findMapping(partialName: string): SourceMapping | undefined {
    // Try exact match first
    const exact = this.mappings.get(partialName);
    if (exact) return exact;

    // Try partial match (case-insensitive)
    const lowerPartial = partialName.toLowerCase();
    for (const [key, value] of this.mappings.entries()) {
      if (key.toLowerCase().includes(lowerPartial)) {
        return value;
      }
    }

    return undefined;
  }

  /**
   * Get all mappings
   */
  getAllMappings(): SourceMapping[] {
    return Array.from(this.mappings.values());
  }

  /**
   * Clear all mappings
   */
  clear(): void {
    this.mappings.clear();
  }

  /**
   * Export mappings to JSON for debugging
   */
  toJSON(): Record<string, SourceMapping> {
    const result: Record<string, SourceMapping> = {};
    for (const [key, value] of this.mappings.entries()) {
      result[key] = value;
    }
    return result;
  }
}

/**
 * Global source map registry instance
 */
export const globalSourceMap = new SourceMapRegistry();
